import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import classNames from "classnames";
import { Field, FormSection, getFormValues } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import AssetSelect from "social/AssetSelect";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { prettifyMessages } from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { YOUTUBE_INSTREAM_CONSTRAINTS } from "social/validators/youtube";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { SOCIAL_WIZARD } from "widgets/forms/list";

import "./YouTubeInStream.css";

const {
	MAX_LENGTH_VIDEO_TITLE,
	MAX_LENGTH_DESCRIPTION,
	MAX_VIDEO_BYTES,
	VIDEO_ACCEPT,
	MAX_THUMBNAIL_BYTES,
	THUMBNAIL_ACCEPT,
} = YOUTUBE_INSTREAM_CONSTRAINTS;

const YOUTUBE_INSTREAM_FIELDS = {
	video: {
		name: "video",
		label: "Video",
		component: AssetSelect,
		required: true,
		className: "full-width cypress-youtube-instream-video",
		assetType: ASSET_TYPES.VIDEO,
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_VIDEO_BYTES)} Max`,
			},
			{
				name: "File Types",
				description: VIDEO_ACCEPT,
			}
		],
		accept: VIDEO_ACCEPT,
	},
	thumbnail: {
		name: "thumbnail",
		label: "Image Thumbnail",
		component: AssetSelect,
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_THUMBNAIL_BYTES)} Max`,
			},
			{
				name: "File Types",
				description: THUMBNAIL_ACCEPT,
			},
		],
		className: "full-width cypress-youtube-instream-thumbnail",
		assetType: ASSET_TYPES.IMAGE,
		accept: THUMBNAIL_ACCEPT,
	},
	name: {
		name: "name",
		label: "Creative Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		required: true,
		className: "full-width cypress-youtube-instream-name",
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
	},
	videoTitle: {
		name: "videoTitle",
		label: "Video Title",
		placeholder: "Enter a video title",
		component: ReduxControls.Input,
		required: true,
		className: "full-width cypress-youtube-instream-video-title",
		charMax: MAX_LENGTH_VIDEO_TITLE,
		showCharCount: true,
	},
	description: {
		name: "description",
		label: "Video Description",
		placeholder: "Enter a description of the video",
		component: ReduxControls.Input,
		required: true,
		className: "full-width cypress-youtube-instream-description",
		charMax: MAX_LENGTH_DESCRIPTION,
		showCharCount: true,
	},
};

// These fields are in it's own row
const videoPrivacyStatusOptions = [
	{
		label: "Unlisted",
		value: "unlisted",
	},
	{
		label: "Public",
		value: "public",
	},
	{
		label: "Private (unusable for ads)",
		value: "private",
	}
];



const adType = SOCIAL_AD_TYPES.YOUTUBE_INSTREAM;

export default function YouTubeInStream(props) {
	const { navigationActions, libraryId, isViewOnly, showPristineErrors } = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const values = formValues[adType] || {};

	const isVideoPrivacyStatusFilled = !!values?.videoPrivacyStatus;

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, YOUTUBE_INSTREAM_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = useMemo(() => (
		Object.values(YOUTUBE_INSTREAM_FIELDS).map((fieldProps) => {
			const extraProps = {};

			if (fieldProps.name === "video" || fieldProps.name === "thumbnail") {
				extraProps.navigationActions = navigationActions;
				extraProps.libraryId = libraryId;
			}

			extraProps.disabled = isViewOnly;

			if (extraProps.disabled && fieldProps.placeholder) {
				// We set this on extraProps, because we don't want to mutate the fieldProps consts
				extraProps.placeholder = '';
			}

			extraProps.showPristineErrors = showPristineErrors;

			return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
		})
	), []);

	return (
		<div className="form-container youtube-in-stream">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}

					<div className="row">
						<div
							className={classNames(
								"required",
								"e-form__control-wrapper",
								"has-label",
								{
									"e-form--valid": isVideoPrivacyStatusFilled,
									"e-form--pristine": !isVideoPrivacyStatusFilled,
								}
							)}
						>
							<div className="e-form__control-header">
								<label className="e-form__control-label">Privacy Status</label>
							</div>
							<div className="video-privacy-radio-group">
								{videoPrivacyStatusOptions.map((option) => {
									return (
										<Field
											key={option.value}
											name="videoPrivacyStatus"
											required
											useValue
											disabled={isViewOnly}
											component={ReduxControls.Checkbox}
											className={`cypress-youtube-instream-${option.value}`}
											type="radio"
											{...option}
										/>
									);
								})}
							</div>
						</div>
					</div>

					<div className="e-form__control-wrapper video-audience">
						<div className="e-form__control-header">
							<label className="e-form__control-label">Video Audience</label>
						</div>

						<Field
							name="madeForKids"
							label="Made for Kids"
							useValue
							required
							disabled={isViewOnly}
							component={ReduxControls.Checkbox}
							className="cypress-youtube-instream-made-for-kids"
							type="checkbox"
						/>
					</div>
				</div>
			</FormSection>
		</div>
	);
}

YouTubeInStream.displayName = "YouTubeInStreamForm";
